/**
 * The ProGST API client.
 *
 * It is a thin layer over `session.authed` from `@fiveox/shell` — that is
 * where the bearer, the single serialized refresh and the one place a session
 * ends already live (see `session.ts`). What this file adds is the two things
 * the shell cannot know about: **the org header** and **this app's error
 * envelope**.
 */

import type { Ledgers } from './gstr3bLedgers'
import { SERVICE_API } from './config'
import { type FetchedFile, fileNameFrom } from './files'
import { SELECTED_ORG_KEY, setSelectedOrgId } from './orgs'
import { session } from './session'
import type { SupplierLedgerOut } from './supplierLedger'
import type { TwoAVsTwoB } from './twoAVsTwoB'

/**
 * A refusal with the machine code the service sent.
 *
 * The code is the contract: a screen switches on `SEAT_LIMIT_EXCEEDED` to say
 * "5 of 5 seats in use" and on `NOT_ENTITLED` to render the paywall. Throwing a
 * bare `Error` here is what produces "something went wrong".
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the refusal is about entitlement rather than about this user. */
  get isPaywall(): boolean {
    return this.code === 'NOT_ENTITLED' || this.code === 'USAGE_LIMIT_EXCEEDED'
  }

  get entitlementReason(): string | undefined {
    const reason = this.details.entitlement_reason
    return typeof reason === 'string' ? reason : undefined
  }
}

// -- the selected org --------------------------------------------------------
//
// A user belongs to many orgs and switches between them, so the selected org
// is **not** derivable from the token: the client sends it and the service
// proves the user may act in it (FiveOX ADR-0029).

let currentOrgId: string | null =
  typeof localStorage === 'undefined' ? null : localStorage.getItem(SELECTED_ORG_KEY)

const orgListeners = new Set<() => void>()

export function setOrgId(orgId: string | null): void {
  if (orgId === currentOrgId) return
  currentOrgId = orgId
  setSelectedOrgId(orgId)
  for (const listener of orgListeners) listener()
}

export const getOrgId = () => currentOrgId

export function subscribeOrg(listener: () => void): () => void {
  orgListeners.add(listener)
  return () => orgListeners.delete(listener)
}

// -- the fetcher -------------------------------------------------------------

/**
 * **`X-Fiveox-Org`** — the header the whole estate speaks. Not `X-Org-Id`:
 * gst-service invented that name and no core service reads it, so a core
 * caller sending the correct header would have been refused. Corrected
 * 2026-08-25.
 *
 * **The new name only** (ADR-0025). The estate renamed `X-Fiveox-Org` by
 * dual-accept; gst-service reads both until the platform's Wave 6, so a stale
 * bundle keeps working, but this header goes to our service and nowhere else,
 * so there is no reader here that needs the old name sent.
 */
const ORG_HEADER = 'X-Fiveox-Org'

/**
 * A file ProGST sends itself (`app/api/files.py`): its bytes, the name to save it as, its hash
 * and how to show it. POST, as the routes are: a prefetch must not log a read nobody made. Not
 * through `api()`, which parses JSON; `session.authed` still owns the bearer and the refresh.
 */
async function fetchFile(path: string, failure: string): Promise<FetchedFile> {
  if (!currentOrgId) {
    throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
  }
  const res = await session.authed(`${SERVICE_API}${path}`, {
    method: 'POST',
    headers: { [ORG_HEADER]: currentOrgId },
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { code?: string; message?: string } }
      | null
    throw new ApiError(body?.error?.code ?? 'INTERNAL', body?.error?.message ?? failure, res.status)
  }
  return {
    blob: await res.blob(),
    filename: fileNameFrom(res.headers.get('content-disposition')),
    sha256: res.headers.get('x-content-sha256'),
    render: res.headers.get('x-render'),
  }
}

export interface Envelope<T> {
  success: boolean
  data: T
  meta?: Record<string, unknown>
}

interface Options extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Multipart upload — sent as-is; the browser writes the boundary header. */
  form?: FormData
  /** For routes that are not org-scoped. */
  withoutOrg?: boolean
  /** A step-up assertion from core-mfa, for an action that cannot be undone. */
  stepUpAssertion?: string
}

export async function api<T>(path: string, options: Options = {}): Promise<Envelope<T>> {
  // **A plain object, not a `Headers` instance — and this is not a style
  // choice.** `@fiveox/shell`'s `authed` merges with
  // `{ ...(init.headers ?? {}), authorization: ... }`, and spreading a
  // `Headers` yields `{}` because it has no enumerable own properties. Passing
  // one silently drops every header except the bearer the shell adds itself.
  //
  // That cost an afternoon: the org header never left the browser, the
  // service refused with 400, the UI read it as `authority_unreachable` and
  // told the user the permission service was down. `content-type` was being
  // lost the same way, so every POST would have followed.
  //
  // Reported upstream — see docs/PLATFORM_INTEGRATION.md §9.3.
  const headers: Record<string, string> = {}
  for (const [key, value] of new Headers(options.headers).entries()) headers[key] = value
  // JSON only when it IS json — a FormData body must not carry a forced
  // content-type, or the multipart boundary never gets written.
  if (options.body !== undefined && !options.form) headers['content-type'] = 'application/json'
  if (!options.withoutOrg) {
    if (!currentOrgId) {
      throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    }
    headers[ORG_HEADER] = currentOrgId
  }
  if (options.stepUpAssertion) headers['X-StepUp-Assertion'] = options.stepUpAssertion

  // `session.authed` owns the bearer, the 401 refresh-and-replay, and ending
  // the session when the refresh comes back spent.
  const res = await session.authed(`${SERVICE_API}${path}`, {
    ...options,
    headers,
    body: options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
  })

  // 204 is an answer with no body: a delete that worked (found 2026-10-13, W3-17 (8d)). Read as
  // JSON it was null, and a removal that succeeded said "Something went wrong."
  if (res.status === 204) return { success: true, data: null as T }

  const body = (await res.json().catch(() => null)) as
    | (Envelope<T> & {
        error?: { code: string; message: string; details?: Record<string, unknown> }
      })
    | null

  if (!res.ok || !body?.success) {
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'Something went wrong.',
      res.status,
      body?.error?.details ?? {},
    )
  }
  return body
}

// -- the shapes the platform half serves -------------------------------------

/**
 * What the GSTIN itself says — offline, deterministic, no credential.
 *
 * Note what is NOT here: `registration_type` and `registration_status`. Neither
 * is encoded in a GSTIN, and registration type gates FR-3, so a field for it on
 * this object would invite exactly the wrong fill.
 */
export interface DerivedGstinFacts {
  state_code: string
  pan: string
  /** The PAN's fourth character in words. `null` for a letter outside the
   *  allocation — the honest answer to a character we do not recognise. */
  entity_type: string | null
  constitution: string | null
  /** This PAN holds more than one registration in this state (character 13). */
  is_additional_registration: boolean
  /** Character 14 is `Z`. Anything else is a different category of
   *  registration, and worth saying rather than assuming away. */
  is_ordinary_taxpayer: boolean
}

/**
 * The public register's answer, in OUR vocabulary — the register's own
 * abbreviations (`lgnm`, `ctb`, `dty`) stop at the service's adapter.
 *
 * Everything past the GSTIN is nullable because a register answer routinely
 * is: a field this omits must stay blank rather than acquire a default, or the
 * record will assert something nobody said.
 */
export interface RegisterProfile {
  legal_name: string | null
  trade_name: string | null
  /** Active / Cancelled / Suspended. Reported, never used to refuse: a
   *  practice onboards a cancelled registration to file its final return. */
  registration_status: string | null
  constitution: string | null
  registration_type: string | null
  registered_on: string | null
  address_line: string | null
  city: string | null
  pincode: string | null
  state_code: string | null
}

/**
 * Two sources, kept apart on purpose. "Proprietorship, read from your GSTIN"
 * and "Proprietorship, per the register" are different claims, and a screen
 * handed one merged object will present the first as the second.
 */
/** A CAPTCHA from the GST portal, for a person to read (ADR-0033). */
export interface PortalCaptcha {
  /** A `data:image/png;base64,` URI — the portal's own picture. */
  image: string
  /** Sealed by the service; sent back with the answer, never read here. */
  token: string
  expires_in: number
}

/** One portal lookup's answer, by either route (ADR-0033). */
export interface PortalSearch {
  /** `found`, or `wrong_captcha` — then `captcha` is a new one to try. */
  result: string
  gstin: string
  register?: RegisterProfile | null
  /** `gst_portal` (fetched through ProGST) or `gst_portal_pasted`. */
  source: string
  fetched_at?: string | null
  captcha?: PortalCaptcha | null
  message?: string | null
}

export interface TaxpayerLookup {
  gstin: string
  derived: DerivedGstinFacts
  /** `null` while ADR-0004's gate is shut — `register_gate` then says why. */
  register: RegisterProfile | null
  register_gate: string | null
  /** The same taxpayer, registered elsewhere, already on this firm's list.
   *  Not a conflict: one PAN across several states is ordinary. */
  also_filed_for_this_pan: {
    id: string
    legal_name: string
    gstin: string
    state_code: string
  }[]
}

export interface ClientRecord {
  id: string
  legal_name: string
  trade_name: string | null
  gstin: string
  state_code: string
  registration_type: string
  status: 'active' | 'archived'
  engagement_owner_user_id: string | null
  created_at: string
  /**
   * D-102 (W3-18 (4)): whether a credit note this client booked that is not yet in the 2B
   * reduces the claim. `null` until it is asked of the client — the 3B says so.
   */
  reduce_booked_credit_notes?: boolean | null
  /**
   * FR-3: which gated surfaces this client's registration type reaches — the service's gate table
   * (`gating.surfaces_for`), served on every client read. `ScreenGate` renders "not yet supported"
   * for a return or reconciliation screen whose flag is not `true`; a record without it is gated.
   */
  surfaces?: { returns: boolean; recon: boolean }
  /**
   * Present only on a single read, and only for a caller holding
   * `gst.client.update`. A list response is cached and proxied far more widely
   * than one client's own screen, and these are personal data.
   */
  pan?: string | null
  address_line?: string | null
  city?: string | null
  pincode?: string | null
  contact_person?: string | null
  contact_phone?: string | null
  contact_email?: string | null
  signatory_name?: string | null
  signatory_designation?: string | null
  constitution?: string | null
}

/** The editable subset — mirrors the service's `ClientPatch`, nothing wider. */
export interface ClientUpdate {
  legal_name?: string
  trade_name?: string | null
  registration_type?: string
  address_line?: string | null
  city?: string | null
  pincode?: string | null
  contact_person?: string | null
  contact_phone?: string | null
  contact_email?: string | null
  signatory_name?: string | null
  signatory_designation?: string | null
  constitution?: string | null
  /** D-102; `null` puts it back to not asked. */
  reduce_booked_credit_notes?: boolean | null
}

export interface ActivityEvent {
  id: string
  at: string
  action: string
  subject_type: string
  subject_id: string | null
  actor_user_id: string | null
  actor_kind: string
  /**
   * As recorded. Carries content keys, reason codes and the reviewer's own
   * words — which is the point, since "why did this row leave the return" is
   * answered by the note and by nothing else. Never a filename.
   */
  // Nullable on the table (`gst_audit_events.detail`), and the Activity screen
  // already reads it as `e.detail ?? {}` — the type claimed a guarantee the
  // column never made. Found by `tests/contract.assert.ts`.
  detail: Record<string, unknown> | null
  /** What happened, in words — the service's (`app/activity.py`), never a hand-kept table. */
  title: string
  /**
   * The detail in words: what the event is about, then the note and the decision. No key, id or
   * hash — the service names what those point at, or leaves them out.
   */
  lines: Array<{ label: string; value: string }>
}

/**
 * A Letter of Undertaking the client furnished (B-01): exports and SEZ supplies without tax.
 * One per financial year, written `2026-27` (D-143); an export reads its invoice's year's.
 */
export interface Lut {
  number: string
  financial_year: string
  recorded_at?: string | null
}

/** A buyer a person confirmed as an SEZ unit or developer (B-03, D-16). */
export interface SezBuyer {
  gstin: string
  derived_from: 'register' | 'person'
  register_says: string | null
  confirmed_at: string | null
}

/** The client's export facts — what its exports and SEZ supplies book by. */
export interface ExportFacts {
  luts: Lut[]
  sez_buyers: SezBuyer[]
  /** Returns sent back for re-approval by the change just made (`GSTR1 07-2026`). */
  demoted_periods: string[]
}

export const exportFacts = {
  get: (clientId: string) => api<ExportFacts>(`/api/v1/clients/${clientId}/export-facts`),
  /** Refused when the year has a LUT already: an export reads ONE LUT, its year's. */
  addLut: (clientId: string, body: { number: string; financial_year: string }) =>
    api<ExportFacts>(`/api/v1/clients/${clientId}/luts`, { method: 'POST', body }),
  removeLut: (clientId: string, financialYear: string) =>
    api<ExportFacts>(
      `/api/v1/clients/${clientId}/luts/${encodeURIComponent(financialYear)}`,
      { method: 'DELETE' },
    ),
  confirmSez: (
    clientId: string,
    body: { gstin: string; derived_from: 'register' | 'person'; register_says?: string | null },
  ) => api<ExportFacts>(`/api/v1/clients/${clientId}/sez-buyers`, { method: 'POST', body }),
  removeSez: (clientId: string, gstin: string) =>
    api<ExportFacts>(`/api/v1/clients/${clientId}/sez-buyers/${encodeURIComponent(gstin)}`, {
      method: 'DELETE',
    }),
}

export const clients = {
  list: (params: { search?: string; status?: string; limit?: number; offset?: number } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<ClientRecord[]>(`/api/v1/clients${suffix}`)
  },
  get: (id: string) => api<ClientRecord>(`/api/v1/clients/${id}`),
  /** Monthly or quarterly (QRMP), effective-dated (FR-E16): now, and every change. */
  filingFrequency: (id: string) =>
    api<FilingFrequency>(`/api/v1/clients/${id}/filing-frequency`),
  /** A change from a quarter's first month; refused where it would strand an opened return. */
  setFilingFrequency: (
    id: string,
    body: { frequency: 'monthly' | 'quarterly'; effective_from: string; note?: string },
  ) =>
    api<FilingFrequency>(`/api/v1/clients/${id}/filing-frequency`, { method: 'POST', body }),
  /**
   * What people did to this client's work, newest first.
   *
   * Reads the audit trail; records nothing. Keyset paging on `at` rather than
   * an offset, because an audit trail grows at the head and an offset page two
   * shifts under the reader every time something happens.
   */
  activity: (id: string, params: { limit?: number; before?: string; action?: string } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<{ events: ActivityEvent[]; next_before: string | null }>(
      `/api/v1/clients/${id}/activity${suffix}`,
    )
  },
  /**
   * Only the fields the service's `ClientPatch` accepts. GSTIN, PAN, state and
   * status are deliberately not among them: the GSTIN is the record's identity
   * under `uq_gst_clients_org_gstin_active` (ADR-0002), and status moves
   * through `/archive` so the count that meters the plan stays correct.
   */
  update: (id: string, body: ClientUpdate) =>
    api<ClientRecord>(`/api/v1/clients/${id}`, { method: 'PATCH', body }),
  create: (body: {
    legal_name: string
    gstin: string
    trade_name?: string
    registration_type?: string
    constitution?: string
    /** E-03: what a register lookup filled and the person kept. Optional. */
    address_line?: string
    city?: string
    pincode?: string
    /** `YYYY-MM-DD` — the portal's `01/07/2017` is converted before it gets here. */
    registered_on?: string
  }) => api<ClientRecord>('/api/v1/clients', { method: 'POST', body }),
  /**
   * ADR-0033, through ProGST: a CAPTCHA from the GST portal for the PERSON to
   * read. The token comes back with their answer; it is opaque here.
   */
  portalCaptcha: (gstin: string) =>
    api<PortalCaptcha>('/api/v1/clients/lookup/portal/captcha', {
      method: 'POST',
      body: { gstin },
    }),
  /** The person's answer, and the search it allows. `wrong_captcha` carries a new one. */
  portalSearch: (token: string, captcha: string) =>
    api<PortalSearch>('/api/v1/clients/lookup/portal', {
      method: 'POST',
      body: { token, captcha },
    }),
  /** ADR-0033, from the person's own tab: the portal's result page as they copied it. */
  portalPasted: (gstin: string, text: string) =>
    api<PortalSearch>('/api/v1/clients/lookup/portal/pasted', {
      method: 'POST',
      body: { gstin, text },
    }),
  /**
   * What the public register says about a GSTIN (FR-C13).
   *
   * Reads; creates nothing. `POST /clients` is still the only thing that makes
   * a record, and every field this fills stays editable — the register
   * suggests, the firm decides who they act for.
   *
   * **It answers with the gate shut.** Only the `register` half waits on
   * ADR-0004; reading a GSTIN needs nobody's permission, so this is a 200 with
   * `register: null` rather than a refusal. Whether to offer the control comes
   * from `meta.taxpayer_lookup.available` on the client list — the screen is
   * TOLD, it does not work it out.
   */
  lookup: (gstin: string) =>
    api<TaxpayerLookup>('/api/v1/clients/lookup', { method: 'POST', body: { gstin } }),
  archive: (id: string) =>
    api<ClientRecord>(`/api/v1/clients/${id}/archive`, { method: 'POST' }),
}

/**
 * The PAN-level grain (ADR-0008). A group aggregates a PAN's registrations, and
 * carries the previous-FY turnover that decides how many HSN digits this
 * taxpayer must report — the figure `hsn.digits` reads and, until now, nothing
 * could set.
 */
export interface TaxpayerGroupRecord {
  id: string
  pan: string
  display_name: string
  previous_fy_turnover_paise: number | null
  turnover_fy: string | null
  member_count?: number
}

/**
 * Three answers kept apart on purpose. `confirmed` — a person joined this
 * client to the group, so its turnover is this client's. `suggested` — the PAN
 * matches a group nobody confirmed, so the figure is NOT yet this client's.
 * `null` — no group for this PAN. The screen renders the difference; it never
 * collapses it into "has a turnover".
 */
export interface GroupForClient {
  state: 'confirmed' | 'suggested' | null
  group: TaxpayerGroupRecord | null
  suggested_from_pan: boolean
}

export const groups = {
  list: () => api<{ groups: TaxpayerGroupRecord[] }>('/api/v1/groups'),
  forClient: (clientId: string) =>
    api<GroupForClient>(`/api/v1/groups/for-client/${clientId}`),
  create: (body: { pan: string; display_name: string }) =>
    api<{ group: TaxpayerGroupRecord }>('/api/v1/groups', { method: 'POST', body }),
  /** The D-16 confirmation write: a PAN match proposes, a person agrees. */
  addMember: (groupId: string, body: { client_id: string; suggested_from_pan: boolean }) =>
    api<{ group: TaxpayerGroupRecord }>(`/api/v1/groups/${groupId}/members`, {
      method: 'POST',
      body,
    }),
  /** Both fields together, or both null to clear and send the rule back to holding. */
  setTurnover: (
    groupId: string,
    body: { previous_fy_turnover_paise: number | null; turnover_fy: string | null },
  ) =>
    api<{ group: TaxpayerGroupRecord }>(`/api/v1/groups/${groupId}/turnover`, {
      method: 'PATCH',
      body,
    }),
}

// -- the domain half's shapes (W1-16 routes; the seam, filling in) -----------

/**
 * FR-15 on the wire: exactly one of `stage` / `held` is present, and a held
 * row carries its `next_action` sentence. The screen renders these; it never
 * re-decides them (ADR-0014).
 */
export interface DocumentRecord {
  id: string
  client_id: string
  source_filename: string | null
  relative_path: string | null
  source_lane: string | null
  period: string | null
  doc_type: string | null
  status: string
  checksum_ok: boolean | null
  confidence: number | null
  producer: string
  producer_version: string
  /** `doc_type` in words, for the Raw Data header — the service's (`app/activity.py`). */
  doc_type_words: string
  /** Who read it — "ProDoc, version a9b9650", never `producer@version`. */
  read_by: string
  parent_id: string | null
  /** The sheet of its workbook this document is (D-82); null for a whole file. */
  sheet_name?: string | null
  // `stage` is nullable on the table. Every row written with a null stage
  // today also carries a hold, so a staged document has one in practice — but
  // nothing enforces that, and the screens already guard it (`row.stage ?? ''`).
  stage?: string | null
  held?: string
  next_action?: string | null
  /** What a reviewer may do about this hold. Served by the service so the
   *  hold vocabulary lives in one place; empty means nothing to click. */
  dispositions?: string[]
  /**
   * Whether "Bind the columns" is an answer for this row — the hold is one a
   * profile may read past AND the slot is one where columns mean anything.
   * Served for the same reason as `dispositions`: this screen used to keep its
   * own copy of the hold list, which could not know that a statement has no
   * columns to bind, and so offered the button under a sentence saying not to.
   */
  bindable?: boolean
  normalized?: Record<string, unknown> | null
  size_bytes: number | null
  at: string | null
  source?: DocumentSource
  /** Null until a person has asserted this document is right. */
  verified?: { at: string; by: string | null; remark: string | null } | null
  /**
   * True while a return would use this document and nobody has approved it (ADR-0035, W3-13).
   * Sent only when true, and only with `GST_REQUIRE_APPROVAL` on — absent means not waiting.
   */
  awaiting_approval?: boolean
}

export interface IntakeSuggestion {
  client_name: string | null
  period: string | null
  lane: string | null
}

/**
 * A client's column maps — kept in ProDoc since X-14 (ADR-0045).
 *
 * The map itself — the sheet, the header row, which column is which — is ProDoc's, made and
 * changed in ProDoc's mapper (`routes/ColumnMapper.tsx`). What ProGST keeps beside it is what the
 * layout MEANS here: what its lines are (`doc_type`) and which trial-balance rows its sheet reports
 * (`ledger_link`, D-94). That is all this list serves.
 */
export interface ColumnMapRecord {
  /** ProDoc's map id — the key ProGST's meaning is kept under (D-162). */
  id: string
  name: string
  /** ProDoc's profile: `sales_register`, `purchase_register`, `trial_balance`, `books_ledger`. */
  profile: string
  /**
   * Field → binding, in ProDoc's words: `header:<text>`, a column's letter, `rate:<head>`, or a
   * list of columns added together. As last saved through ProGST.
   */
  columns: Record<string, unknown>
  doc_type: string
  /** Null until it has read something. An unused map is a signal. */
  last_used_at: string | null
  /** The trial-balance rows this sheet reports when its lines name no ledger (D-94). */
  ledger_link?: TrialBalanceRowRef[] | null
}

/**
 * What ProDoc's mapper opens on for one file (X-14 (l)). The service asks ProDoc; this file only
 * carries the answer across — `mapping` is ProDoc's own (the sheets, the header candidates and the
 * suggestion), handed to the mapper as it came.
 */
export interface DocumentMapping {
  /** ProDoc's document, not ProGST's: the mapper previews against it. */
  document_id: string
  version_id: string | null
  profile: string
  /** `client:<id>` — whose map a save makes. The service's, never built here. */
  scope: string
  sheet: string | null
  mapping: Record<string, unknown>
  /** The map that read this file, when one did (D-43): Save adds a version to it. */
  existing_map_id?: string | null
}

export const columnMaps = {
  list: (clientId: string) =>
    api<{ maps: ColumnMapRecord[] }>(`/api/v1/column-maps?client_id=${clientId}`),
  /**
   * What the mapper needs for this file. `CONFLICT` for a row that has nothing to map, and
   * `APP_UNAVAILABLE` when ProDoc does not answer — both said by the dialog in words.
   */
  forDocument: (documentId: string) =>
    api<DocumentMapping>(`/api/v1/documents/${documentId}/mapping`),
  /**
   * After a person saved a map: ProDoc reads the file again under the client's maps, and the
   * reading supersedes the row (a new document, ADR-0012). A refusal keeps ProDoc's new mapping
   * on the held row, and its message says why.
   */
  readAgain: (documentId: string) =>
    api<DocumentRecord>(`/api/v1/documents/${documentId}/read-again`, { method: 'POST' }),
  /**
   * *Use* one of the client's saved maps on this file (the binding dialog's buttons, kept beside
   * ProDoc's mapper): ProDoc reads it under that one map, or refuses `CONFLICT` with ProGST's
   * sentence saying why it does not fit — never another map, never a guess.
   */
  applyMap: (documentId: string, mapId: string) =>
    api<DocumentRecord>(`/api/v1/documents/${documentId}/apply-map`, {
      method: 'POST',
      body: { map_id: mapId },
    }),
  /**
   * The trial-balance rows a sheet that names no ledger reports (D-94). Kept beside its map, so
   * next month's sheet is linked with no person.
   */
  linkLedger: (mapId: string, rows: TrialBalanceRowRef[]) =>
    api<ColumnMapRecord>(`/api/v1/column-maps/${mapId}/ledger-link`, {
      method: 'PUT',
      body: { rows },
    }),
  unlinkLedger: (mapId: string) =>
    api<ColumnMapRecord>(`/api/v1/column-maps/${mapId}/ledger-link`, {
      method: 'DELETE',
    }),
}

export const documents = {
  list: (params: { client_id?: string; stage?: string; held?: string; period?: string } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<DocumentRecord[]>(`/api/v1/documents${suffix}`)
  },
  get: (id: string) => api<DocumentRecord>(`/api/v1/documents/${id}`),
  dispose: (id: string, body: { disposition: string; note?: string }) =>
    api<DocumentRecord>(`/api/v1/documents/${id}/dispose`, { method: 'POST', body }),
  reclassify: (id: string, body: { doc_type: string }) =>
    api<DocumentRecord>(`/api/v1/documents/${id}/reclassify`, { method: 'POST', body }),
  /** Removes an upload outright. Refused once its rows are booked — dispose then. */
  remove: (id: string) => api<null>(`/api/v1/documents/${id}`, { method: 'DELETE' }),
  /**
   * A freshly-minted read URL for the ORIGINAL upload. A capability: minted per
   * open, never stored, never put in an href on load. POST because a GET could
   * be prefetched, which would mint one nobody asked for and record a view that
   * never happened.
   */
  sourceUrl: (id: string) =>
    api<{ url: string; expires_in: number; sha256: string; render: string }>(
      `/api/v1/documents/${id}/source-url`,
      { method: 'POST' },
    ),
  /**
   * The ORIGINAL upload's bytes, sent by ProGST, for *Show document*. What the pane shows; the
   * link above went to an address that answered with the sign-in page on UAT (2026-10-06).
   */
  sourceFile: (id: string) =>
    fetchFile(`/api/v1/documents/${id}/source`, 'The source could not be opened.'),
}

export const intake = {
  /** Names only, before any bytes move — a warning, never a refusal. */
  precheck: (filenames: string[], laneDirection: 'sales' | 'purchase') =>
    api<{ mismatches: Array<{ filename: string; looks_like: string; reason: string }> }>(
      '/api/v1/intake/precheck',
      { method: 'POST', body: { filenames, lane_direction: laneDirection } },
    ),
  suggest: (relativePath: string) =>
    api<IntakeSuggestion>(
      `/api/v1/intake/suggest?relative_path=${encodeURIComponent(relativePath)}`,
    ),
  upload: (input: {
    file: File
    client_id: string
    period: string
    relative_path?: string
    lane?: string
  }) => {
    const form = new FormData()
    form.set('file', input.file, input.file.name)
    form.set('client_id', input.client_id)
    form.set('period', input.period)
    if (input.relative_path) form.set('relative_path', input.relative_path)
    if (input.lane) form.set('lane', input.lane)
    return api<DocumentRecord>('/api/v1/intake/files', { method: 'POST', form })
  },
}

/** What the server says can be shown for a document's original upload. */
export interface DocumentSource {
  /** False when the file was never kept — not "lost", never captured. */
  available: boolean
  /** Decided by the SERVICE, never re-derived here from a filename. */
  render: 'pdf' | 'image' | 'none'
  content_type: string
}

export interface ReviewRow {
  content_key: string
  /** field → 'user' | 'rule', for every value an overlay is responsible for. */
  overlaid?: Record<string, string>
  /** Set when a person took this row out of the return. */
  excluded_by?: { reason_code: string; note: string } | null
  /**
   * Set when a person ADDED this row — no extraction produced it. The screen
   * marks it, because a row with no document behind it must never be
   * indistinguishable from one read off a page.
   */
  added_by?: { reason_code: string; note: string } | null
  /** Set when a preparer raised this row with the client (D-79). It stays in the return. */
  raised_by?: { note: string } | null
  /**
   * Set while the GST portal's last word on this row is a rejection (FR-E6): its code and
   * message, from the error report uploaded on Returns. The row also fails `portal_rejected`
   * and is held until a correction after the rejection answers it.
   */
  rejected_by?: { error_cd: string | null; error_msg: string | null; section: string } | null
  /**
   * The row reads as an export (FR-E9, B-02): its shipping bill can be added here even though
   * it is accepted — customs often issues the bill after the register is read.
   */
  is_export?: boolean
  /**
   * GSTR-1 holds this sale for a place of supply nobody stated (D-144), so it is not clean
   * here either, and **Correct…** is offered for it.
   */
  needs_place_of_supply?: boolean
  /**
   * GSTR-1 holds this sale for the invoice type it states (D-148): one the service does not
   * read, or one that contradicts the tax, the LUT, the place of supply or another source.
   * `why` names both sides. **Correct…** is offered on it, opening on the invoice type.
   */
  invoice_type_hold?: { code: string; why: string } | null
  /**
   * The row states an SEZ invoice type for a buyer the client's SEZ list does not hold, so
   * *Remember this buyer as SEZ* is offered (D-80, D-148).
   */
  remember_sez?: boolean
  /** `MM-YYYY` when the row is dated outside its document's month (D-123), else null. */
  outside_month?: string | null
  /**
   * Set when a person included that row in the month (D-123): `by` is `document` (the whole
   * document, at intake) or `row` (here, in Review).
   */
  included_by?: { note: string; month: string; by: 'document' | 'row' } | null
  /** True when the register says the bill was cancelled (D-124): in Table 13, sold nowhere. */
  cancelled?: boolean
  /** D-131: the bill's split into its taxed part and an exempt part (paise), else null. */
  split_by?: { taxed: number; exempt: number; rate: number; note: string } | null
  /** D-131: the bill's own row key when this row is the untaxed part of a split bill. */
  split_from?: string | null
  /**
   * D-151: GSTR-1 holds this sale for its rupees (`needs_inr_value`) — it states a foreign
   * currency and nothing gives its rupees — so it is not clean, and **Convert at a rate…** is
   * offered.
   */
  needs_inr_value?: boolean
  /**
   * D-151: how a person converted this invoice — the rate (rupees for one unit, as typed), the
   * reason, who (`by`, a user id; `entered_by`, their name or email when they typed it).
   */
  converted_by?: {
    currency: string
    rate: string
    reason_code?: string | null
    note?: string | null
    entered_by?: string | null
    by?: string | null
    at?: string | null
  } | null
  /** What the producer said, before any overlay. `effective` is what it says now. */
  extracted: Record<string, unknown>
  effective: Record<string, unknown>
  failing: string[]
  conflicts: Array<Record<string, unknown>>
  accepted: boolean
}

export interface ReviewSuggestion {
  content_key: string
  field: string
  value: unknown
  rule_id: string
  reason: string
  kind: string
  computed: boolean
}

export interface ReviewFlag {
  content_key: string
  field: string
  reason: string
  /** What a person needs to decide, where the reason alone cannot say it (W3-12 (4a)). */
  detail?: string
  /**
   * The codes a person may pick from, each with GSTN's description: the directory's codes
   * under one the client's summary gave and GSTN does not publish.
   */
  choices?: { code: string; description: string }[]
}

/**
 * Where one row stands against the current GSTR-2B.
 *
 * `held` is the state that is neither of the other two: the row never reached
 * the matcher, because `recon/books.py` offers it only rows that pass their
 * checks. Rendering it as "not in 2B" would be a claim about a comparison
 * nobody made.
 *
 * `missing_in_books` is the mirror, and only ever appears on a REFERENCE
 * document's rows: the 2B has this invoice and the register does not, so the
 * ITC is sitting unclaimed.
 */
export interface ReviewReconRow {
  state: 'matched' | 'unmatched' | 'held' | 'missing_in_books'
  /** Present on `matched` only. */
  rung?: 'exact' | 'relaxed' | 'fuzzy' | 'manual'
  status?: 'accepted' | 'suggested' | 'stale' | 'rejected'
  evidence?: Record<string, unknown> | null
}

export interface ReviewRecon {
  /**
   * `no_import` — nobody has told us what the portal holds for this period.
   * `not_matched_yet` — a 2B is imported and matching has not run on it, so no row has been
   * found absent; `rows` is empty rather than every row reading "not in GSTR-2B" (2026-09-29).
   */
  state: 'imported' | 'no_import' | 'not_matched_yet'
  import_version?: number | null
  /** Keyed by `content_key`, filtered to THIS PAGE like `flags` is. */
  rows: Record<string, ReviewReconRow>
}

export interface ReviewBody {
  document_id: string
  /** N30: `sales`, `purchase` or `reference` — which party the rows name, for the grouping. */
  direction?: string | null
  /**
   * ONE PAGE of rows. A real register is 40,568 lines and every one carries at
   * least a classification suggestion, so the unpaged read was 53 MB and the
   * screen rendered 7.7 million characters of it.
   */
  rows: ReviewRow[]
  /** The whole document's row count — never the page's. */
  total_rows: number
  limit: number
  offset: number
  /**
   * Whole-document tallies, deliberately NOT paged. `rows`, `suggestions` and
   * `flags` describe what is in front of you; these describe what there is, so
   * a reviewer looking at two hundred rows can still see that 19,899 need an
   * HSN. Never compute a total by counting the arrays above.
   */
  suggestion_counts: Record<string, number>
  flag_counts: Record<string, number>
  /** Whole-document tally of recon states, by the same rule as the two above. */
  recon_counts: Record<string, number>
  /**
   * A register ProGST read itself: what its own total row says, and — W3-12 (1)
   * — how many rows under that total were left unread because the register
   * ends there (a summary table, not sales). Null for anything else.
   */
  /**
   * The register's own TOTAL row against the lines read (`profile.Attestation`): `attested` is the
   * file's total, `summed` the lines', `deltas` the difference, per head, in paise. A platform
   * report states one figure instead (`stated`, `rows`).
   */
  register_totals?: {
    outcome?: string
    below_total?: number
    attested?: Record<string, number>
    summed?: Record<string, number>
    deltas?: Record<string, number>
    row?: number | null
    stated?: number
    rows?: number
  } | null
  orphaned: Array<{ content_key: string; field: string }>
  /** Every field named on this page, in words — the service's (`app/activity.py`). */
  field_words: Record<string, string>
  suggestions: ReviewSuggestion[]
  flags: ReviewFlag[]
  recon: ReviewRecon
  /**
   * The fields a correction may touch — the SERVICE's list, served rather than
   * duplicated here. It used to be a byte-identical copy in this file, which is
   * one edit away from the two disagreeing about what may be changed.
   */
  correctable_fields: string[]
  /** D-144: the Indian places of supply a row's is picked from, as GSTN labels them. */
  places_of_supply?: string[]
  /** D-148: the six invoice types a row's is picked from, code → the words a person reads. */
  invoice_types?: Record<string, string>
  /**
   * What a money correction may touch, in integer PAISE — served, never
   * duplicated here. Split from `rate_fields` so the form can show a rupee
   * placeholder without this file deciding which fields are money.
   */
  money_fields: string[]
  /** Correctable alongside money and deliberately NOT money: a percent, a count. */
  rate_fields: string[]
  /** code → label. The same dict the service validates against. */
  money_reasons: Record<string, string>
  /** code → label: why a row is taken out (N34), **Take it out**'s own list, validated the same way. */
  exclude_reasons: Record<string, string>
  /** code → label: why a person typed a conversion rate (D-151, G21 = A), validated the same way. */
  rate_reasons?: Record<string, string>
  /** What a reviewer may state on a row they add — served, never duplicated. */
  addable_fields: string[]
  /**
   * Whether this document is on the approval track, so a row added to it would
   * actually reach the return. The SERVICE answers; this file does not work it
   * out from a stage list of its own.
   */
  can_add_rows: boolean
  /**
   * Whether "Reclassify & resubmit" can work for this document. It was offered
   * on every document while the running producer refused every reclassify
   * (C4) — so the screen asks, as it does for `can_add_rows`.
   */
  can_reclassify: boolean
  /**
   * Whether this file was read through a saved layout that can be corrected
   * and the file read again through it (D-43). Asked, not worked out here.
   */
  can_rebind: boolean
  /** A trial balance's rows, its month's documents and its warnings (W3-17 (8d)). */
  trial_balance?: TrialBalanceScreen
}

/** One trial-balance row and what became of it this month (W3-17 (8d)). Money in paise. */
export interface TrialBalanceRow {
  /** `code:<code>` for a ledger, `name:<folded description>` for a carve-out. */
  key: string
  code: string | null
  name: string
  remark: string | null
  /** In percent, as read (D-95); `rate_written` is the cell as the sheet writes it. */
  rate: number | null
  rate_written: string | null
  carve_out: boolean
  month: number
  reported: number
  by: string[]
  /** Word for word, as the practice wrote them beside the row (D-94). */
  notes: string[]
  words: string
  holds: string[]
  line_key: string | null
  /** A carve-out marked as not carved from exempt income (D-95). */
  marked: boolean
}

/** One of the month's other sales documents, as the trial balance sees it (D-94). */
export interface TrialBalanceDocument {
  document_id: string | null
  label: string
  reports: 'by_line' | 'linked' | 'unlinked' | 'not_a_sheet' | 'awaits_columns' | string
  /** The ProDoc column map that read it (ADR-0045), which a link is kept beside; null when none did. */
  profile_id: string | null
  linked: Array<Record<string, string>>
  awaits_approval: boolean
}

export interface TrialBalanceScreen {
  sheet: string
  month_heading: string | null
  rows: TrialBalanceRow[]
  /** Hold code -> its words, the service's (`returns/reasons.py`). */
  hold_words: Record<string, string>
  documents: TrialBalanceDocument[]
  warnings: string[]
}

/** A trial-balance row as a link names it: a ledger by its code, a carve-out by its name. */
export type TrialBalanceRowRef = { code: string } | { name: string }

/**
 * A person's standing answers about a trial balance's rows (D-95): a carve-out that is not carved
 * from exempt income, remembered for the client.
 */
export const trialBalanceMarks = {
  mark: (clientId: string, rowName: string) =>
    api<{ row_name: string; mark: string }>('/api/v1/trial-balance-marks', {
      method: 'PUT',
      body: { client_id: clientId, row_name: rowName, mark: 'not_from_exempt' },
    }),
  unmark: (clientId: string, rowName: string) =>
    api<null>(
      `/api/v1/trial-balance-marks?${new URLSearchParams({
        client_id: clientId,
        row_name: rowName,
        mark: 'not_from_exempt',
      })}`,
      { method: 'DELETE' },
    ),
}

export const review = {
  /**
   * Override the producer's arithmetic — several fields at once, because
   * fixing `taxable` alone leaves the row contradicting itself. Values go as
   * strings; the service coerces them to paise at a named boundary.
   */
  correctMoney: (
    id: string,
    body: {
      content_key: string
      values: Record<string, string>
      reason_code: string
      note: string
    },
  ) =>
    api<{ applied: Record<string, { old: unknown; new: number }>; demoted_periods: string[] }>(
      `/api/v1/review/${id}/money-corrections`,
      { method: 'POST', body },
    ),
  /** Take a row out of the return, or put it back. Append-only either way. */
  /**
   * Add a line the parser missed (Stage 4). The one call in this file that can
   * create money from nothing — which is why the client workbook discloses
   * every row it produces, and why the note is required and the client reads it.
   */
  addRow: (
    id: string,
    body: { line: Record<string, unknown>; reason_code: string; note: string },
  ) =>
    api<{ content_key: string; demoted_periods: string[] }>(
      `/api/v1/review/${id}/additions`,
      { method: 'POST', body },
    ),

  exclude: (
    id: string,
    body: { content_key: string; excluded: boolean; reason_code: string; note: string },
  ) =>
    api<{ excluded: boolean; demoted_periods: string[] }>(
      `/api/v1/review/${id}/exclusions`,
      { method: 'POST', body },
    ),
  /**
   * D-79: raise a row with the client, or withdraw it. The row stays in the return; the note
   * is the question the client reads on their workbook, so raising needs one.
   */
  raiseWithClient: (
    id: string,
    body: { content_key: string; raised: boolean; note: string },
  ) =>
    api<{ raised: boolean; demoted_periods: string[] }>(
      `/api/v1/review/${id}/queries`,
      { method: 'POST', body },
    ),
  /** D-131: split a bill only partly taxed, or undo it; the service works the split out. */
  /**
   * D-151: convert a foreign-currency invoice at a rate a person typed — rupees for one unit, as
   * text, with a reason (`rate_reasons`; `other` needs a note) — or undo it. Every row of the
   * invoice converts together.
   */
  convert: (
    id: string,
    body: {
      content_key: string
      converted: boolean
      rate?: string
      reason_code?: string
      note?: string
    },
  ) =>
    api<{
      converted: boolean
      currency: string | null
      rate: string | null
      rows: number
      demoted_periods: string[]
    }>(`/api/v1/review/${id}/conversions`, { method: 'POST', body }),
  splitBill: (id: string, body: { content_key: string; split: boolean }) =>
    api<{ split: boolean; demoted_periods: string[] }>(`/api/v1/review/${id}/splits`, {
      method: 'POST',
      body,
    }),
  /**
   * D-123: include a row dated in another month in this one, or withdraw that. The row is then
   * routed as the month's with its own date; including needs the reason, which the working sheet
   * and the client workbook print beside it.
   */
  includeInMonth: (
    id: string,
    body: { content_key: string; included: boolean; note: string },
  ) =>
    api<{ included: boolean; month: string; demoted_periods: string[] }>(
      `/api/v1/review/${id}/inclusions`,
      { method: 'POST', body },
    ),
  /**
   * One person, one document: *I have looked at this and I stand behind it.*
   * The remark is required and whitespace is refused server-side — an empty
   * confirmation confirms nothing.
   */
  verify: (id: string, body: { remark: string }) =>
    api<{ verified: boolean; demoted_periods: string[] }>(
      `/api/v1/review/${id}/verify`,
      { method: 'POST', body },
    ),
  /**
   * The resolution queue as a workbook (X-09 Stage 3).
   *
   * Not through `api()`: that parses JSON and this response is a spreadsheet.
   * Headers are assembled the same way and `session.authed` still owns the
   * bearer and the refresh-and-replay.
   */
  hsnQueue: async (clientId: string, period?: string): Promise<Blob> => {
    if (!currentOrgId) {
      throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    }
    const query = period ? `?period=${encodeURIComponent(period)}` : ''
    const res = await session.authed(
      `${SERVICE_API}/api/v1/review/hsn-queue/${clientId}${query}`,
      { method: 'GET', headers: { [ORG_HEADER]: currentOrgId } },
    )
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { code?: string; message?: string } }
        | null
      throw new ApiError(
        body?.error?.code ?? 'INTERNAL',
        body?.error?.message ?? 'The queue could not be built.',
        res.status,
      )
    }
    return res.blob()
  },
  /**
   * Send the filled sheet back. One bad code fails the whole file — the
   * service applies nothing rather than half of it, so a failure here means
   * the client's data is exactly as it was.
   */
  applyHsnQueue: (clientId: string, file: File) => {
    const form = new FormData()
    form.set('file', file, file.name)
    return api<{
      products: number
      rows: number
      documents: number
      skipped_unchanged: number
      demoted_periods: string[]
    }>(`/api/v1/review/hsn-queue/${clientId}`, { method: 'POST', form })
  },
  /**
   * Every HSN repair that is arithmetic rather than judgement — a zero a
   * spreadsheet dropped (usually the leading one; a trailing one only when that
   * completion alone is in the directory), with exactly one code owning the
   * digits (the owner's narrow exception to D-16, 2026-09-14). A row a
   * person already decided is never touched, and the answer names what is LEFT
   * (`needs_a_person`). Idempotent. Unreachable until 2026-09-28 (finding A4),
   * while 109 such codes held 10,818 rows out of one real August's Table 12.
   */
  repairHsn: (clientId: string) =>
    api<HsnRepair>(`/api/v1/review/hsn-queue/${clientId}/repair`, { method: 'POST' }),
  /**
   * Remember what this client's own registers consistently say about each
   * product's code (Q2, owner 2026-09-13), so a line carrying only a heading
   * is PROPOSED the full code under `hsn.from_history` — never applied, and
   * never presented as a person's decision. Headings, products coded two ways
   * and products a person decided are skipped. Unreachable until 2026-09-28
   * (finding A5).
   */
  seedHsn: (clientId: string) =>
    api<HsnSeed>(`/api/v1/review/hsn-queue/${clientId}/seed`, { method: 'POST' }),
  get: (documentId: string, params: { limit?: number; offset?: number } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<ReviewBody>(`/api/v1/review/${documentId}${suffix}`)
  },
  correct: (documentId: string, body: { content_key: string; field: string; value: unknown }) =>
    api<{ applied: boolean }>(`/api/v1/review/${documentId}/corrections`, {
      method: 'POST',
      body,
    }),
  /**
   * One field corrected on every live row of the document, all or nothing (D-66) — what the
   * preview's header fields will call. Each row gets its own audited correction, committed
   * together, so the rows never disagree.
   */
  correctEveryRow: (documentId: string, body: { field: string; value: unknown }) =>
    api<{ rows: number; demoted_periods: string[] }>(
      `/api/v1/review/${documentId}/corrections/every-row`,
      { method: 'POST', body },
    ),
  accept: (
    documentId: string,
    body: {
      content_key: string
      field: string
      value: unknown
      rule_id: string
      kind?: string
      computed?: boolean
      /** Teaches the UQC master, which is keyed by HSN (FR-22). */
      hsn?: string
      /** Teaches the HSN memory, which is keyed by the product (X-09). */
      item?: string
    },
  ) => api<{ accepted: boolean }>(`/api/v1/review/${documentId}/accept`, { method: 'POST', body }),
}

/** One side of a suggested or stale pair — money in paise, as every recon figure. */
export interface MatchSide {
  supplier_gstin: string | null
  doc_no: string | null
  doc_date: string | null
  taxable: number | null
  igst: number | null
  cgst: number | null
  sgst: number | null
  cess: number | null
}

export interface ReconMatchOut {
  id: string
  status: string
  rung: string
  books_content_key: string
  tb_row_key: string
  tolerance: { name: string | null; value: Record<string, unknown> | null }
  evidence: Record<string, unknown> | null
  /** `null` when that row is no longer there — a superseded document, an
   *  earlier import. Absent from a service older than 2026-09-29. */
  books?: MatchSide | null
  tb?: MatchSide | null
}

export interface WorklistEntry {
  content_key?: string
  tb_row_key?: string
  supplier_gstin: string | null
  doc_no: string | null
  doc_date: string | null
  taxable: number | null
  age_days: number | null
  risk: string
  next_actions: string[]
  /** D-38: the month a books-only purchase was booked, when it is not this one. */
  carried_from?: string | null
  /** FR-E27: the IMS state a person set on a 2B-only row, or null. Older services omit it. */
  ims?: ImsStateOut | null
}

/** FR-E27: a 2B document's IMS state as the service serves it (`recon/ims.py::ImsState`). */
export interface ImsStateOut {
  state: string
  /** The state in the service's words — the chip prefers its own label table. */
  words: string
  /** `user` — set here; `portal` — read off the portal's own 2B. */
  source: 'user' | 'portal'
  reason_code: string | null
  /** The reason in words, or null. */
  reason: string | null
  note: string | null
  /** The action to revert, when a person set it. */
  action_id: string | null
  /** The month it was set in; it stands from then on. */
  period_set: string | null
  pushed_at: string | null
}

/** One row of the IMS worklist: a state set, a portal rejection, or a disagreement. */
export interface ImsRow extends ImsStateOut {
  tb_row_key: string
  row: MatchSide | null
  doc_kind: string | null
  /** What the latest 2B shows: on a `(Rejected)` sheet, on the statement, or absent. */
  portal: 'rejected' | 'carried' | 'absent'
  /** One sentence where the portal and ProGST differ, or null. */
  disagreement: string | null
  /** D-11: the state the portal's IMS holds, as *Get IMS* last answered; null when not read. */
  portal_ims?: string | null
  portal_ims_words?: string | null
}

/** A row taken off a worklist by Suppress, and how to put it back. */
export interface SuppressedRow {
  action_id: string
  side: 'books' | 'tb'
  row: MatchSide | null
  /** The Suppress reason, in words. */
  reason: string
  note: string | null
  at: string
}

export interface ReconState {
  import: { id: string; version_no: number; row_count: number; diff: Record<string, number> } | null
  suggested: ReconMatchOut[]
  stale: ReconMatchOut[]
  worklists: {
    books_only: WorklistEntry[]
    tb_only: WorklistEntry[]
    /** Rows a person took off, still off — with the action to revert (N8). Older services omit it. */
    suppressed?: SuppressedRow[]
    /** FR-E27: the IMS rows of the month. Older services omit it. */
    ims?: ImsRow[]
  }
  itc: {
    eligible: Record<string, number>
    held_stale: Record<string, number>
    disputed: Record<string, number>
    suppressed: Record<string, number>
    fallback: { state: string; gate: string }
    /** FR-E27: how many 2B documents rejected or pending in IMS count for nothing (`lib/ims.ts`). */
    ims?: Record<string, unknown>
  }
  books_notes: string[]
  /** `invoice`, or `supplier` when the month's books carry a party-wise register (D-33). */
  mode: string
  /** The party-wise registers that put the month in supplier mode, by file name. */
  party_wise: string[]
  /** Supplier mode only: the ledger 4A was claimed from, with its pairings (W3-09 e). */
  suppliers?: SupplierLedgerOut | null
}

/** What an import answered: the version it became and the door it came through. */
export interface ReconImportOut {
  import_id: string
  version_no: number
  row_count: number
  diff: Record<string, number>
  /** `gstr2b` or `gstr2a` (FR-E28). Older services omit it. */
  kind?: string | null
  /** `workbook`, `json` or `rows` (FR-E26). Older services omit it. */
  source?: string | null
}

function statementForm(input: { file: File; client_id: string; period: string }): FormData {
  const form = new FormData()
  form.set('file', input.file, input.file.name)
  form.set('client_id', input.client_id)
  form.set('period', input.period)
  return form
}

export const recon = {
  /** The portal's own 2B workbook — the file operators actually download. */
  importWorkbook: (input: { file: File; client_id: string; period: string }) =>
    api<ReconImportOut>('/api/v1/recon/import-workbook', {
      method: 'POST',
      form: statementForm(input),
    }),
  /**
   * The portal's 2B JSON download (FR-E26) — and still the JSON array of
   * already-normalised rows the `.json` arm took before, which the service
   * tells apart and records as `source: rows`.
   */
  importJson: (input: { file: File; client_id: string; period: string }) =>
    api<ReconImportOut>('/api/v1/recon/import-json', {
      method: 'POST',
      form: statementForm(input),
    }),
  /** The portal's GSTR-2A, .xlsx or .json, stored beside the 2B and claimed from by nothing (FR-E28). */
  import2a: (input: { file: File; client_id: string; period: string }) =>
    api<ReconImportOut>('/api/v1/recon/import-2a', {
      method: 'POST',
      form: statementForm(input),
    }),
  /** The month's 2A against its 2B, each row with its reason (FR-E28). */
  twoAVsTwoB: (clientId: string, period: string) =>
    api<TwoAVsTwoB>(
      `/api/v1/recon/2a-vs-2b?client_id=${clientId}&period=${encodeURIComponent(period)}`,
    ),
  state: (clientId: string, period: string) =>
    api<ReconState>(`/api/v1/recon?client_id=${clientId}&period=${encodeURIComponent(period)}`),
  /**
   * The month as a workbook — six invoice sheets, both sides on every row; for a party-wise
   * month, the supplier ledger by supplier instead (W3-09).
   *
   * `session.authed` directly rather than `api()`, for the same reason
   * `hsnQueue` does: the body is bytes, not the JSON envelope, so there is
   * nothing to unwrap and a failure arrives as JSON in an otherwise binary
   * response.
   */
  exportWorkbook: async (
    clientId: string,
    period: string,
  ): Promise<Pick<FetchedFile, 'blob' | 'filename'>> => {
    if (!currentOrgId) {
      throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    }
    const res = await session.authed(
      `${SERVICE_API}/api/v1/recon/export?client_id=${clientId}` +
        `&period=${encodeURIComponent(period)}`,
      { method: 'GET', headers: { [ORG_HEADER]: currentOrgId } },
    )
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { code?: string; message?: string } }
        | null
      throw new ApiError(
        body?.error?.code ?? 'INTERNAL',
        body?.error?.message ?? 'The reconciliation could not be built.',
        res.status,
      )
    }
    // The name the service gives it (D-58, D-127): `<Client> - GSTR-2B Reconciliation -
    // <Month Year>.xlsx`, so it sits with the client's other files.
    return { blob: await res.blob(), filename: fileNameFrom(res.headers.get('content-disposition')) }
  },
  /**
   * Rows already in the import's own shape, posted as JSON. The screen's
   * `.json` arm went through here until FR-E26; it goes through `importJson`
   * now, which takes the same array as well as the portal's download. Kept
   * because the route is served and an API caller may hold rows of its own.
   */
  importRows: (body: {
    client_id: string
    period: string
    rows: Array<Record<string, unknown>>
    source_filename?: string
  }) => api<ReconImportOut>('/api/v1/recon/import', { method: 'POST', body }),
  run: (body: { client_id: string; period: string }) =>
    api<{ matched: number; suggested: number }>('/api/v1/recon/run', { method: 'POST', body }),
  act: (body: {
    type: 'accept' | 'link' | 'suppress' | 'value_accept' | 'revert' | 'pair' | 'ims'
    client_id: string
    period: string
    match_id?: string
    action_id?: string
    books_content_key?: string
    tb_row_key?: string
    reason_code?: string
    heads?: Record<string, string>
    note?: string
    /** `pair`: the register's GSTIN as typed, and the 2B GSTIN it was suggested as (D-44). */
    books_gstin?: string
    tb_gstin?: string
    /** `ims` (FR-E27): the state set on the 2B document `tb_row_key` names; `reason_code` is then an `ims_reason`. */
    ims_state?: 'accepted' | 'rejected' | 'pending' | 'no_action'
  }) => api<{ action_id?: string; match_id?: string }>('/api/v1/recon/actions', { method: 'POST', body }),
}

export interface ReferenceValueOut {
  key: string
  value: Record<string, unknown> | null
  placeholder: boolean
  source_note: string
}

export const reference = {
  category: (category: string, period: string) =>
    api<{ values: ReferenceValueOut[] }>(
      `/api/v1/reference/${category}?period=${encodeURIComponent(period)}`,
    ),
}

export interface PeriodRecord {
  id: string
  client_id: string
  /** `GSTR1A` (FR-E13, B-07): a same-period correction of a filed GSTR-1, its own period. */
  return_type: 'GSTR1' | 'GSTR3B' | 'GSTR1A'
  period_type: string
  period: string
  state: string
  artifact_sha256: string | null
  /** The acknowledgment that MARKED this filed. A person's word (FR-9). */
  filed_ack_no: string | null
  /** When the portal receipt CONFIRMED it. Null means marked, not confirmed. */
  filed_confirmed_at?: string | null
  /**
   * `prepared` — computed here. `snapshot` — a month filed before ProGST held
   * these books: stored, shown, never recomputed (ND-3).
   */
  origin?: 'prepared' | 'snapshot'
  history?: Array<{
    action: string
    detail: Record<string, unknown>
    actor_user_id: string | null
    at: string
  }>
}

type Heads = Record<'taxable' | 'igst' | 'cgst' | 'sgst' | 'cess', number>

/** One amended document: what was filed, what the books now say, and the
 * difference (`revised − filed`, the prepared-vs-filed convention). */
export interface AmendmentRow {
  original_period: string
  ctin: string | null
  doc_no: string | null
  doc_date: string | null
  /** Place of supply — the key of a table with no counterparty GSTIN. */
  pos?: string | null
  /** A consolidated row's rate (B2CSA, ATA, TXPA). */
  rate?: number | null
  /** What a document amendment points at: the portal's own number and date. */
  original_doc_no?: string | null
  original_doc_date?: string | null
  /** Table 14A: the operator restated. */
  operator_name?: string
  filed: Heads
  revised: Heads
  diff: Heads
}

/** Every amendment table `build_amendments` can return (B-05, FR-E12). */
export type AmendmentTable =
  | 'b2ba'
  | 'b2cla'
  | 'b2csa'
  | 'cdnra'
  | 'cdnura'
  | 'expa'
  | 'ata'
  | 'txpa'
  | 'ecoa'

/** `returns.amendments.build_amendments`. Two arms: refused with a reason
 * (`applicable: false`), or the tables. */
export interface Amendments {
  applicable: boolean
  reason?: string
  message?: string
  original_period?: string
  amending_period?: string
  convention?: string
  tables: Partial<Record<AmendmentTable, AmendmentRow[]>>
  /** Tables whose base table this app does not book yet — named, never shown empty. */
  unbuilt?: Partial<Record<AmendmentTable, string>>
  unchanged?: number
  new_in_this_period?: Array<{ section: string; doc_no: string | null; ctin: string | null }>
  filed_not_in_books?: Array<{ section: string; doc_no: string | null; ctin: string | null } & Heads>
}

/** `AmendmentsOut` on the service. */
/** `api.periods.MonthPointOut` — a point about the month as a whole (D-130). */
export interface MonthPoint {
  id: string
  subject: string
  /** The subject in the return's own words — what the client reads as the point's item. */
  what: string
  note: string
  raised_at: string
  actor_user_id?: string
}

/** `api.periods.MonthPointsOut`. `demoted_periods` comes back on a raise or a withdrawal only. */
export interface MonthPointsBody {
  points: MonthPoint[]
  /** Every subject a point can name, key → words, for the list. */
  subjects: Record<string, string>
  demoted_periods?: string[]
}

export interface AmendmentsBody {
  period: PeriodRecord
  client_name: string
  filed_gstin: string
  /** `upload` or `stored` — where the filed return being amended came from. */
  filed_source: string
  filed_artifact_id: string | null
  amendments: Amendments
  /** The build kept on this period, which its utility workbook and JSON write (B-06);
   *  null when the build was refused. */
  amendments_artifact_id: string | null
}

/** What seeding HSN memory did — `HsnSeedOut` on the service. */
export interface HsnSeed {
  /** Products the client's own history could answer. */
  seeded: number
  /** Earlier seeded rows withdrawn because the history no longer supports them. */
  withdrawn: number
  /** Products carrying two or more different full codes. */
  skipped_ambiguous: number
  /** Products coded under unrelated chapters. */
  skipped_contradictory: number
  /** Products whose only codes were headings. */
  skipped_short: number
  /** Products a person already decided — never overwritten. */
  skipped_decided: number
}

/** What an HSN repair run did — `HsnRepairOut` on the service. */
export interface HsnRepair {
  codes: number
  rows: number
  documents: number
  /** What the arithmetic could not settle — the queue's work. */
  needs_a_person: number
  /** Rows a person had already decided, left alone. */
  respected_decisions: number
  /** Broken code -> the code it was restored to. */
  repaired: Record<string, string>
  unrepairable_sample: string[]
  demoted_periods: string[]
}

/** The filed return taken against a period — `FiledUploadOut` on the service. */
export interface FiledUpload {
  gstin: string
  fp: string
  document_count: number
  storage_uri: string
  artifact_id: string
  version_no: number
  sha256: string
  /** False when the period was already confirmed, or is not marked filed. */
  filing_confirmed: boolean
  /**
   * Sent for a GSTR-3B (e2e-filing C-02): the filed 3B is stored and confirms the filing, and
   * nothing in it is read — the reader is Wave E's — so the figures on screen stay ProGST's.
   */
  note?: string
}

/** One of the portal's errors, as the report named it (`rejections` / `unmatched`). */
export interface PortalRejection {
  section: string
  ctin?: string | null
  doc_no?: string | null
  pos?: string | null
  rate?: number | null
  error_cd?: string | null
  error_msg?: string | null
}

/** What landing the portal's error report answered — `ErrorReportOut` on the service (FR-E6). */
export interface ErrorReportUpload {
  gstin: string | null
  fp: string | null
  /** Distinct register lines now held with the portal's message. */
  rows_held: number
  errors_matched: number
  errors_unmatched: number
  /** Each held line, named as the gates panel names a row, plus the portal's words. */
  held: Array<PortalRejection & Record<string, unknown>>
  /** Every error no line matched — listed, never dropped. */
  unmatched: PortalRejection[]
  /** Whether this upload sent the return back to Prepared. False when it already was. */
  demoted: boolean
  state: string
  sha256: string
}

/** A freshly generated artifact — `GeneratedArtifactOut` on the service. */
export interface GeneratedArtifact {
  id: string
  kind: string
  version_no: number
  sha256: string
  status: string
}

/**
 * FR-E13 (e2e-filing B-07): the GSTR-1A side of a month, on the gates read. On a filed GSTR-1,
 * what has changed since filing and the 1A if one is open; on a GSTR-1A, its own draft and the
 * GSTR-1 it corrects. `available: false` carries the reason (no filed side held). Money in paise.
 */
export interface Gstr1aBlock {
  available: boolean
  reason?: string | null
  changes?: number | null
  added?: Array<Record<string, unknown>>
  amended?: Array<Record<string, unknown>>
  /** Filed documents the books no longer carry — listed, never written. */
  deleted?: Array<Record<string, unknown>>
  unchanged?: number | null
  /** Section -> why its rows cannot be compared; non-empty holds the 1A payload. */
  uncompared?: Record<string, string>
  filed_source?: string | null
  filed_artifact_id?: string | null
  convention?: string | null
  gstr1a_period_id?: string | null
  gstr1_period_id?: string | null
}

export interface PeriodGates {
  period: PeriodRecord
  client_name: string
  blocks_prepared: boolean
  /**
   * This return's own documents waiting for a person's approval (ADR-0035, W3-13). Sent only when
   * any wait, which needs `GST_REQUIRE_APPROVAL` on; while any do, Prepare is refused.
   */
  awaiting_approval?: Array<{ id: string; source_filename: string | null; period: string | null }>
  /**
   * FR-E2 (e2e-filing A-06): the GSTN JSON could not be written at the last attempt — the draft
   * books a section the writer has none for. Sent only while the period is past client approval
   * and no current JSON exists; `words` is the sentence Activity says too.
   */
  gstn_json_hold?: { section: string; sections_omitted: Record<string, string>; words: string; at: string }
  /** FR-E13 (B-07): sent on a filed GSTR-1 and on a GSTR-1A. */
  gstr1a?: Gstr1aBlock
  blocked_table12: Array<Record<string, unknown>>
  /** Table 12 lines kept and marked (D-85, D-86): a code GSTN does not publish, goods reported as OTH. They do not block. */
  marked_table12?: Array<Record<string, unknown>>
  tieout: Array<Record<string, unknown>>
  pending: Array<Record<string, unknown>>
  excluded: Array<Record<string, unknown>>
  totals: Record<string, Record<string, number>>
  nil: Record<string, number>
  docs: Record<string, Record<string, unknown>>
  artifacts: Array<{
    id: string
    kind: string
    version_no: number
    status: string
    sha256: string
    generated_from_state: string
    out_of_date_because?: string
  }>
}

/**
 * The return's sections, row by row. Section keys the draft does not build
 * are simply absent, and the screen renders a tab only for what is here,
 * which is how the legacy behaves too.
 *
 * This used to add "B2CL waits on V-01, ECO on V-11". Both were building by
 * then; what was actually missing was the tab. Corrected 2026-09-26, along
 * with the same sentence in `Periods.tsx` and `api/periods.py` — three copies
 * of one stale excuse, which is why a vocabulary now has one declaration and
 * a test.
 */
/** Table 11's standing on a month (FR-E11): a state, never a zero. */
export interface AdvancesState {
  state: 'none_declared' | 'held' | 'booked'
  entries: number
  booked: number
}

/**
 * Exports (6A), SEZ supplies (6B) and deemed exports (6C) on a month (FR-E9, FR-E10): `none`
 * when the month has none of them — every other key is then absent.
 */
export interface ExportsState {
  state: 'none' | 'held' | 'booked'
  /** D-143: the LUT numbers by financial year the month's rows could read. */
  luts?: Record<string, string>
  export_invoices?: number
  sez_invoices?: number
  deemed_invoices?: number
  /** Exports whose document states no shipping bill yet — correct it on Review. */
  shipping_bill_pending?: string[]
}

export interface PeriodTables {
  period: PeriodRecord
  tables: Record<string, unknown>
  totals: Record<string, { taxable?: number; igst?: number; cgst?: number; sgst?: number }>
  rule_stamp: Record<string, number>
  advances: AdvancesState
  exports: ExportsState
}

/** What a typed advance recorded (FR-E11, `POST /periods/{id}/advances`). */
export interface AdvanceEntry {
  document_id: string
  kind: 'advance' | 'adjustment'
  pos: string
  rate: number
  /** Paise. */
  amount: number
  date: string
  words: string
  demoted: boolean
  state: string
}

type CheckHeads = { taxable: number; igst: number; cgst: number; sgst: number; cess: number }

/** One code and rate on Table 12, ours beside the client's (W3-12 (4a)). */
export interface ClientGstr1Row {
  hsn: string
  rate: number | null
  ours: CheckHeads
  theirs: CheckHeads
  /** `ours − theirs`. */
  diff: CheckHeads
  ok: boolean
  /** Which side has the code at all, where only one does. */
  only: 'ours' | 'theirs' | null
}

/** Our Table 12 against the client's own GSTR-1 HSN summary (D-50). */
export interface ClientGstr1Check {
  period: PeriodRecord
  present: boolean
  source_filename?: string
  comparison?: {
    layout: string | null
    gstin: string | null
    period_from: string | null
    period_to: string | null
    sections: Array<{
      section: 'b2b' | 'b2c' | 'all'
      rows: ClientGstr1Row[]
      ours: CheckHeads
      theirs: CheckHeads
      diff: CheckHeads
      ok: boolean
    }>
    problems: Array<Record<string, unknown> & { code: string }>
    waiting_on_codes: number
    ok: boolean
    /** GSTN's Excel layout only (D-72): the month it states, `MM-YYYY`. */
    month?: string | null
    /** GSTN's Excel layout only (D-72): every other table, ours beside the client's. */
    tables?: ClientGstr1TableLine[]
    /**
     * W3-12 (4b-2), P5: the newest upload to Portal GSTR-1 is byte for byte a utility workbook
     * ProGST generated — said so, never compared. With no client's file before it, `sections`
     * is empty and `ok` false.
     */
    own_file?: ClientGstr1OwnFile
  }
}

/** Which of ProGST's own generated workbooks an upload is (W3-12 (4b-2), P5). */
export interface ClientGstr1OwnFile {
  filename: string | null
  artifact_id: string | null
  version: number | null
  /** The period the workbook was generated for, `MM-YYYY` or `Qn-YYYY-YY`. */
  period: string | null
}

/** One table (or one row of Table 8 / 13), ours beside the client's, on what the
 * template states for it (W3-12 (4b-1)). Money in paise; counts as counts. */
export interface ClientGstr1TableLine {
  table: 'b2b' | 'b2cl' | 'b2cs' | 'cdnr' | 'cdnur' | 'exp' | 'at' | 'atadj' | 'nil' | 'docs'
  row: string | null
  fields: string[]
  ours: Record<string, number>
  theirs: Record<string, number>
  diff: Record<string, number>
  ok: boolean
}

export interface Gstr3bBody {
  period: PeriodRecord
  client_name: string
  client_gstin: string | null
  gstr3b: {
    period: string
    cells: Record<string, Record<string, unknown>>
    setoff?: {
      cash?: Record<string, number>
      utilisation?: Record<string, Record<string, number>>
      credit_remaining?: Record<string, number>
    }
    [k: string]: unknown
  }
  tieout: { ok: boolean; discrepancies: Array<Record<string, unknown>> }
  /**
   * The 3B as it is filed (C-08, C-09): the same cells in whole rupees (integer paise, multiples
   * of 100), 6.1 set off again on them, what holds the GSTR-3B JSON, the states filed as nil,
   * and the challan. The screen prints these so it shows the JSON's and the return sheet's
   * numbers (FR-46).
   */
  return_view?: Gstr3bReturnView
  /** C-05/C-06: the portal's ledger balances the month uses; null for a ledger = not entered. */
  ledgers?: Ledgers
}

/** One major head of the challan, by PMT-06 minor head. `null` is not computed, never zero. */
export interface ChallanHead {
  tax: number
  interest: number | null
  penalty: number
  fee: number | null
  others: number
  total: number
}

export interface Gstr3bChallan {
  by_head: Record<string, ChallanHead>
  total: number
  complete: boolean
  /** Minor head (`interest`, `fee`) → why it is not in the total. */
  stated: Record<string, string>
  cash_ledger: string
  sentence: string
}

export interface Gstr3bReturnView {
  cells: Record<string, Record<string, unknown>>
  setoff: {
    cash?: Record<string, number>
    utilisation?: Record<string, Record<string, number>>
    credit_remaining?: Record<string, number>
  }
  holds: Record<string, string>
  stated_as_nil: Record<string, string>
  challan: Gstr3bChallan
}

export interface LedgerBalanceTyped {
  id: string
  period: string
  ledger: string
  heads: Record<string, number> | null
  note: string
  at: string
  demoted_periods: string[]
}

export interface Gstr3bTypedFigure {
  id: string
  period: string
  cell: string
  heads: Record<string, number>
  note: string
  at: string
  demoted_periods: string[]
}

export interface FiledHeads {
  taxable: number
  igst: number
  cgst: number
  sgst: number
  cess: number
}

export interface FiledComparisonBody {
  period: PeriodRecord
  client_name: string
  filed_gstin: string | null
  comparison: {
    convention: string
    ok: boolean
    sections: Array<{
      section: string
      prepared: FiledHeads
      filed: FiledHeads
      diff: FiledHeads
      ok: boolean
    }>
    mismatched: Array<Record<string, unknown>>
    prepared_only: Array<Record<string, unknown>>
    filed_only: Array<Record<string, unknown>>
    /** Filed sections read and counted but not reconciled (exports, Table 13, the
     *  amendment sections) — section → rows. Table 11 is compared since B-12a; its
     *  exception rows carry `words`, a sentence in place of a document number. */
    not_compared?: Record<string, number>
  }
}

export interface TbReconBody {
  period: PeriodRecord
  client_name: string
  sheet: string
  reconciliation: {
    convention: string
    summary: {
      tb_taxable: number
      tb_exempt: number
      tb_non_gst: number
      gstr1_taxable: number
      gstr1_tax: number
      gap: number
      unclassified: number
    }
    income: Array<Record<string, unknown>>
    gst: Array<{ particulars: string; amount: number }>
    exempt: Array<Record<string, unknown>>
    unclassified: Array<Record<string, unknown>>
  }
}

/**
 * One obligation on the calendar (FR-E7): `returns/calendar.py::Obligation` as the wire
 * carries it. `days` is signed — negative still to come, positive late. `late_fee` is `null`
 * where none applies, and carries its own `parameters_unverified` flag (D-14). `nil_basis` is
 * one of the service's `NIL_BASES`; `lib/calendar.ts` gives each its words.
 */
export interface ObligationRecord {
  client_id: string
  client_name: string
  return_type: string
  period: string
  due_date: string
  due_source: string
  state: string
  days: number
  late_fee: Record<string, unknown> | null
  interest: Record<string, unknown>
  is_nil: boolean
  nil_basis: string
}

/** A client's filing frequency (FR-E16). `since` is null for the monthly default. */
export interface FilingFrequency {
  frequency: string
  since: string | null
  as_of_month: string
  history: Array<{
    frequency: string
    effective_from: string
    note: string | null
    actor_user_id: string | null
    recorded_at: string
  }>
}

/** One return a client files for a month (`GET /periods/openable`, FR-E16). */
export interface OpenableRecord {
  return_type: string
  period_type: string
  period: string
  label: string
}

/** The PMT-06 deposit for a quarterly client's month 1 or 2 (FR-E16). Money in paise. */
export interface Pmt06Record {
  client_id: string
  period: string
  quarter: string
  quarter_words: string
  due_date: string
  due_source: string
  self_assessment: {
    state: string
    cash_paise: Record<string, number>
    total_paise: number
    says: string
  }
  fixed_sum: Record<string, unknown>
}

export const periods = {
  /** The returns this client files for a month, by its frequency in force (FR-E16). */
  openable: (clientId: string, month: string) =>
    api<OpenableRecord[]>(
      `/api/v1/periods/openable?client_id=${encodeURIComponent(clientId)}&month=${encodeURIComponent(month)}`,
    ),
  pmt06: (clientId: string, month: string) =>
    api<Pmt06Record>(
      `/api/v1/periods/pmt06?client_id=${encodeURIComponent(clientId)}&month=${encodeURIComponent(month)}`,
    ),
  list: (
    params: { client_id?: string; state?: string; filed_unconfirmed?: boolean } = {},
  ) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value) query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<PeriodRecord[]>(`/api/v1/periods${suffix}`)
  },
  /**
   * The calendar: every obligation across clients, most overdue first, filed and locked last
   * (FR-E7). With `client_id`, that client's alone. Paged; `meta.total` is the whole count.
   */
  obligations: (params: { client_id?: string; limit?: number; offset?: number } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<ObligationRecord[]>(`/api/v1/periods/obligations${suffix}`)
  },
  /**
   * A month filed before ProGST held these books (X-05). The period is read
   * from the return's own `fp`, so there is nothing to type: the file says
   * which month it is.
   */
  importSnapshot: (body: { client_id: string; file: File; ack_no?: string }) => {
    const form = new FormData()
    form.set('client_id', body.client_id)
    form.set('file', body.file, body.file.name)
    if (body.ack_no) form.set('ack_no', body.ack_no)
    return api<PeriodRecord & { artifact_id: string; document_count: number }>(
      '/api/v1/periods/snapshots',
      { method: 'POST', form },
    )
  },
  get: (id: string) => api<PeriodRecord>(`/api/v1/periods/${id}`),
  open: (body: {
    client_id: string
    return_type: string
    period: string
    period_type?: string
  }) =>
    api<PeriodRecord>('/api/v1/periods', { method: 'POST', body }),
  gates: (id: string) => api<PeriodGates>(`/api/v1/periods/${id}/gates`),
  /** The section tabs' read — rows, not just totals. Fetched when a tab opens. */
  tables: (id: string) => api<PeriodTables>(`/api/v1/periods/${id}/tables`),
  /** Our Table 12 against the client's own HSN summary, where one was uploaded. */
  clientGstr1Check: (id: string) =>
    api<ClientGstr1Check>(`/api/v1/periods/${id}/client-gstr1-check`),
  gstr3b: (id: string) => api<Gstr3bBody>(`/api/v1/periods/${id}/gstr3b`),
  /**
   * A figure a person types into the 3B, with a required note (C-03, V-d): 4(B)(1), 4(B)(2),
   * and the additions to 4(D)(1) and 4(D)(2). The newest entry for a cell is the one read.
   */
  typeGstr3bFigure: (
    id: string,
    body: { cell: string; heads: Partial<Record<'igst' | 'cgst' | 'sgst' | 'cess', number>>; note: string },
  ) =>
    api<Gstr3bTypedFigure>(`/api/v1/periods/${id}/gstr3b/inputs`, { method: 'POST', body }),
  /** C-05: a portal ledger balance, typed with its note; `heads: null` clears it. */
  typeLedgerBalance: (
    id: string,
    body: {
      ledger: 'credit' | 'cash'
      heads: Partial<Record<'igst' | 'cgst' | 'sgst' | 'cess', number>> | null
      note: string
    },
  ) =>
    api<LedgerBalanceTyped>(`/api/v1/periods/${id}/gstr3b/ledgers`, { method: 'POST', body }),
  /** The books (TB sheet of the Main GST Data File) against the return. */
  tbReconciliation: (id: string, file: File) => {
    const form = new FormData()
    form.set('file', file, file.name)
    return api<TbReconBody>(`/api/v1/periods/${id}/tb-reconciliation`, { method: 'POST', form })
  },
  /**
   * The portal's filed return (Returns Dashboard → GSTR-1 → Download Filed
   * Return, a `.json`), uploaded against a period marked filed — which
   * **confirms the filing** (FR-9 step two) and keeps the file as a versioned
   * `filed_return` artifact. Unreachable until 2026-09-28 (finding A1), while
   * the screen listed every filed period as `· unconfirmed`.
   */
  confirmFiling: (periodId: string, file: File) => {
    const form = new FormData()
    form.set('file', file, file.name)
    return api<FiledUpload>(`/api/v1/periods/${periodId}/filed`, { method: 'POST', form })
  },
  /**
   * The error report the portal returned on a rejected GSTR-1 upload (a `.json`), landed on
   * the rows it names (FR-E6): each is held with the portal's words, an error no row matches
   * is listed, and the return goes back to Prepared with the report as the reason. A filed
   * return refuses (FR-9). `parse_error_report` was wired to no route until 2026-10-08.
   */
  uploadErrorReport: (periodId: string, file: File) => {
    const form = new FormData()
    form.set('file', file, file.name)
    return api<ErrorReportUpload>(`/api/v1/periods/${periodId}/error-report`, {
      method: 'POST',
      form,
    })
  },
  /**
   * The cross-period amendment tables (B2BA, B2CSA, CDNRA — FR-31) for this
   * period, against an EARLIER month's filed return: named by
   * `original_period` (the stored snapshot or confirmed filing answers for
   * itself) or uploaded as the portal's `.json`, which wins when both are
   * given. Read-only. Unreachable until 2026-09-28 (finding A2), while the
   * Returns screen told every snapshot an amendment could reference it.
   */
  amendments: (periodId: string, from: { originalPeriod?: string; file?: File }) => {
    const form = new FormData()
    if (from.originalPeriod) form.set('original_period', from.originalPeriod)
    if (from.file) form.set('file', from.file, from.file.name)
    return api<AmendmentsBody>(`/api/v1/periods/${periodId}/amendments`, { method: 'POST', form })
  },
  /** The points raised with the client about this month, still standing (D-130). */
  monthPoints: (id: string) => api<MonthPointsBody>(`/api/v1/periods/${id}/month-points`),
  /** Raise a point about the month as a whole; a sent month's GSTR-1 goes back to Prepared. */
  raiseMonthPoint: (id: string, body: { subject: string; note: string }) =>
    api<MonthPointsBody>(`/api/v1/periods/${id}/month-points`, { method: 'POST', body }),
  /** Withdraw a raised point; it is kept, stamped withdrawn, and audited. */
  withdrawMonthPoint: (id: string, pointId: string) =>
    api<MonthPointsBody>(`/api/v1/periods/${id}/month-points/${pointId}/withdraw`, {
      method: 'POST',
    }),
  /** Prepared vs the return as actually filed on the portal. */
  filedComparison: (id: string, file: File) => {
    const form = new FormData()
    form.set('file', file, file.name)
    return api<FiledComparisonBody>(`/api/v1/periods/${id}/filed-comparison`, {
      method: 'POST',
      form,
    })
  },
  /**
   * Several artifacts as one ZIP. Packaging, not generation — every member
   * was produced at a lifecycle transition and carries its bound SHA.
   * An empty list means every current artifact.
   */
  artifactsZip: async (periodId: string, artifactIds: string[]): Promise<Blob> => {
    // Not through `api()`: that parses JSON, and this response is a ZIP.
    // The headers are assembled the same way, and `session.authed` still owns
    // the bearer and the refresh-and-replay.
    const headers: Record<string, string> = { 'content-type': 'application/json' }
    if (!currentOrgId) {
      throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    }
    headers[ORG_HEADER] = currentOrgId
    const res = await session.authed(
      `${SERVICE_API}/api/v1/periods/${periodId}/artifacts/zip`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ artifact_ids: artifactIds }),
      },
    )
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as
        | { error?: { code?: string; message?: string } }
        | null
      throw new ApiError(
        body?.error?.code ?? 'INTERNAL',
        body?.error?.message ?? 'The bundle could not be built.',
        res.status,
      )
    }
    return res.blob()
  },
  /**
   * The GSTR-3B working paper (W3-08d, ADR-0031) — three ITC lines, and which
   * one is claimed. **On demand**, unlike GSTR-1's workbooks: its use is before
   * the numbers are settled. Each call is a new version that supersedes the
   * last; it lands in `gates.artifacts` and downloads like every artifact.
   * Unreachable from any screen until 2026-09-28 (finding A3).
   */
  generate3bWorking: (periodId: string) =>
    api<GeneratedArtifact>(`/api/v1/periods/${periodId}/gstr3b/working`, { method: 'POST' }),
  /**
   * The GSTR-3B JSON and its return sheet, made now as the next versions (C-09). Client
   * approval makes both; before it the service refuses, naming the state (FR-44). A cell
   * that is a state holds the JSON: the refusal names the cell and the sheet is still made.
   */
  generateGstr3bJson: (periodId: string) =>
    api<GeneratedArtifact>(`/api/v1/periods/${periodId}/artifacts/gstr3b-json`, {
      method: 'POST',
    }),
  /**
   * The challan working (C-08): cash per major head by PMT-06 minor head after the Rule 88A
   * set-off, made now as the next version, in any state.
   */
  generateGstr3bChallan: (periodId: string) =>
    api<GeneratedArtifact>(`/api/v1/periods/${periodId}/artifacts/gstr3b-challan`, {
      method: 'POST',
    }),
  /**
   * GSTN's offline-utility workbook as the practice fills it, made now as the next version
   * (D-59). Prepare makes one; this makes another after the data changed. Every version is kept.
   */
  generateOfflineUtility: (periodId: string) =>
    api<GeneratedArtifact>(`/api/v1/periods/${periodId}/artifacts/offline-utility`, {
      method: 'POST',
    }),
  /**
   * Type one advance received, or one adjusted against an invoice, into a month with no
   * advances register (FR-E11, Table 11). Stored as a one-line document in the Advances
   * lane and reviewed like a register's row; a month already sent for approval goes back
   * to Prepared (FR-7). `amount` is rupees as typed.
   */
  enterAdvance: (
    periodId: string,
    body: {
      kind: 'advance' | 'adjustment'
      pos: string
      rate: number
      amount: string
      date?: string
      note?: string
    },
  ) => api<AdvanceEntry>(`/api/v1/periods/${periodId}/advances`, { method: 'POST', body }),
  /**
   * The practice's GSTR-1 working sheet (D-53), made now as the next version (D-59): the
   * computation, the return view, the HSN summary, the register and one voucher list per bucket.
   */
  generateWorkingSheet: (periodId: string) =>
    api<GeneratedArtifact>(`/api/v1/periods/${periodId}/artifacts/working-sheet`, {
      method: 'POST',
    }),
  /**
   * The GSTN JSON — the portal payload — made now as the next version (FR-E1, e2e-filing
   * A-02). Client approval makes one; this makes another on demand after it. Before the
   * client has approved the service refuses, naming the state (FR-44).
   */
  generateGstnJson: (periodId: string) =>
    api<GeneratedArtifact>(`/api/v1/periods/${periodId}/artifacts/gstn-json`, {
      method: 'POST',
    }),
  /**
   * The GSTR-1A payload (FR-E13, B-07) made now as the next version — the 1A's own GSTN JSON.
   * Client approval of the 1A makes one; before it the service refuses, naming the state.
   */
  generateGstr1aJson: (periodId: string) =>
    api<GeneratedArtifact>(`/api/v1/periods/${periodId}/artifacts/gstr1a-json`, {
      method: 'POST',
    }),
  /**
   * Open the GSTR-1A for a filed GSTR-1's month (FR-E13), or get the one already open.
   * Refuses on a locked, unfiled or snapshot month.
   */
  openGstr1a: (periodId: string) =>
    api<PeriodRecord>(`/api/v1/periods/${periodId}/gstr1a`, { method: 'POST' }),
  /**
   * A freshly-minted read URL for one artifact. A capability, never stored. The browser
   * saves it as `filename` (D-58): `<Client> - <file> - <Month Year> - vN`.
   */
  artifactUrl: (periodId: string, artifactId: string) =>
    api<{ url: string; kind: string; version_no: number; sha256: string; filename: string }>(
      `/api/v1/periods/${periodId}/artifacts/${artifactId}/download`,
    ),
  /**
   * One artifact's bytes, sent by ProGST, under its D-58 name. What the Download buttons save;
   * the link above went to an address that answered with the sign-in page on UAT (2026-10-06).
   */
  artifactFile: (periodId: string, artifactId: string) =>
    fetchFile(
      `/api/v1/periods/${periodId}/artifacts/${artifactId}/file`,
      'That download could not be prepared.',
    ),
  transition: (id: string, body: { action: string; artifact_sha256?: string; ack_no?: string; remark?: string }) =>
    api<PeriodRecord>(`/api/v1/periods/${id}/transition`, { method: 'POST', body }),
  sendForApproval: (id: string) =>
    api<
      PeriodRecord & {
        artifacts: Record<string, { sha256: string; version_no: number }>
        /** FR-E15: whether the client's approval link went out, and the sentence saying so. */
        client_link?: { sent: boolean; words: string }
      }
    >(
      `/api/v1/periods/${id}/send-for-approval`,
      { method: 'POST' },
    ),
}

// -- the filing lane: the GST portal session, per client (D-04, D-05) -------

/** `off`, `sandbox` or `production`, and why it is off in one sentence (ADR-0037 ND-E1). */
export interface FilingLane {
  mode: string
  sentence: string | null
}

export interface FilingSessionState {
  /** `none` | `awaiting_otp` | `live` | `reauth_due` | `expired`. */
  state: string
  /** The service's sentence for the state. */
  words: string
  environment: string | null
  otp_requested_at: string | null
  authenticated_at: string | null
  expires_at: string | null
  reauth_at: string | null
}

export interface FilingState {
  client_id: string
  lane: FilingLane
  /** `••••abc` — the username itself never reaches the browser (NFR-E1). */
  username_masked: string | null
  username_storable: boolean
  /** `EVC` or `DSC` — how this client signs at filing (ADR-0037 ND-E3, D-09). */
  signature: string
  session: FilingSessionState
}

// -- a period through the lane: save → poll → submit → proceed → file (D-07..D-09, D-18) --

export interface FilingSubmission {
  id: string
  /** `save` | `submit` | `proceed` | `file` — labelled on screen; `words` says what happened. */
  action: string
  /** `pending` | `accepted` | `rejected` | `filed`. */
  status: string
  words: string
  /** A rejection's errors, each one sentence with the portal's own message. */
  errors: string[]
  at: string | null
  resolved_at: string | null
  current: boolean
  can_check: boolean
}

export interface PeriodFilingView {
  period_id: string
  return_type: string
  lane: FilingLane
  signature: string
  signature_words: string
  session_live: boolean
  /** What can happen next; the panel picks its buttons from it and shows `next_words`. */
  next: string
  next_words: string
  submissions: FilingSubmission[]
  /** Only after `file`: whether the portal's filed list confirmed it straight away. */
  confirmed?: boolean | null
}

export const periodFiling = {
  view: (periodId: string) => api<PeriodFilingView>(`/api/v1/periods/${periodId}/filing`),
  /** RETSAVE. Answers pending; acceptance arrives only from RETSTATUS. */
  save: (periodId: string) =>
    api<PeriodFilingView>(`/api/v1/periods/${periodId}/filing/save`, { method: 'POST' }),
  /** *Check now* — one RETSTATUS for a pending submission. */
  check: (periodId: string, submissionId: string) =>
    api<PeriodFilingView>(
      `/api/v1/periods/${periodId}/filing/submissions/${submissionId}/check`,
      { method: 'POST' },
    ),
  /** RETSUBMIT — irreversible on the portal; needs a step-up assertion (XC-01). */
  submit: (periodId: string, stepUpAssertion: string) =>
    api<PeriodFilingView>(`/api/v1/periods/${periodId}/filing/submit`, {
      method: 'POST',
      stepUpAssertion,
    }),
  proceed: (periodId: string, stepUpAssertion: string) =>
    api<PeriodFilingView>(`/api/v1/periods/${periodId}/filing/proceed`, {
      method: 'POST',
      stepUpAssertion,
    }),
  /** EVCOTP — the portal sends the OTP to the authorised signatory. */
  evcOtp: (periodId: string) =>
    api<PeriodFilingView>(`/api/v1/periods/${periodId}/filing/evc-otp`, { method: 'POST' }),
  /** RETFILE with the EVC OTP the signatory read out. The OTP is sent once, kept nowhere. */
  file: (periodId: string, otp: string, stepUpAssertion: string) =>
    api<PeriodFilingView>(`/api/v1/periods/${periodId}/filing/file`, {
      method: 'POST',
      body: { otp },
      stepUpAssertion,
    }),
  /** FR-9 step two through the lane: the portal's filed list must show the ARN. */
  confirm: (periodId: string) =>
    api<PeriodFilingView>(`/api/v1/periods/${periodId}/filing/confirm`, { method: 'POST' }),
  /** D-10: a GSTR-3B's RETSAVE of its current JSON. Answers pending, as GSTR-1's does. */
  saveThreeB: (periodId: string) =>
    api<PeriodFilingView>(`/api/v1/periods/${periodId}/filing/gstr3b/save`, { method: 'POST' }),
  /** D-10: RETOFFSET — irreversible; refused with the shortfall per head when the cash
   *  ledger cannot pay. Needs a step-up assertion. */
  offsetThreeB: (periodId: string, stepUpAssertion: string) =>
    api<PeriodFilingView>(`/api/v1/periods/${periodId}/filing/gstr3b/offset`, {
      method: 'POST',
      stepUpAssertion,
    }),
  /** D-10: RETFILE of a GSTR-3B with the EVC OTP. */
  fileThreeB: (periodId: string, otp: string, stepUpAssertion: string) =>
    api<PeriodFilingView>(`/api/v1/periods/${periodId}/filing/gstr3b/file`, {
      method: 'POST',
      body: { otp },
      stepUpAssertion,
    }),
}

/** D-11..D-13: one call to the GST portal outside save → file, as the status screen reads it. */
export interface PortalFetch {
  id: string
  kind: string
  /** What the call was, in words. */
  what: string
  state: 'waiting' | 'done' | 'failed' | string
  state_words: string
  /** What it came to, in one or two sentences. */
  sentence: string
  period: string | null
  financial_year: string | null
  at: string | null
  finished_at: string | null
  polls: number
  /** The one explicit action the row offers: *Check again* or *Try again*. */
  next: { action: 'check' | 'retry' | string; label: string } | null
  import_id: string | null
  artifact_id: string | null
}

/** D-13: a save, submit, proceed or file D-07 sent for the period, in words. */
export interface PortalSubmission {
  id: string
  /** The return's own period — *Check now* is D-07's check on it. */
  period_id: string
  return_words: string
  period_words: string
  action_words: string
  state_words: string
  sentence: string
  arn?: string | null
  /** The portal's error report on a rejection, each error a sentence. */
  errors: string[]
  at?: string | null
  next?: { action: string; label: string } | null
}

export interface FilingMonth {
  client_id: string
  period: string
  financial_year: string
  lane: FilingLane & { owed: string[] }
  session: { state: string; words: string; expires_at: string | null; reauth_at: string | null }
  submissions: PortalSubmission[]
  submissions_sentence: string
  fetches: PortalFetch[]
}

export interface PortalPreference {
  fetch: PortalFetch
  portal: Record<string, string>
  words: string[]
}

export const filing = {
  state: (clientId: string) => api<FilingState>(`/api/v1/filing/${clientId}`),
  setUsername: (clientId: string, username: string) =>
    api<FilingState>(`/api/v1/filing/${clientId}/username`, {
      method: 'PUT',
      body: { username },
    }),
  /** EVC or DSC (ADR-0037 ND-E3) — a client fact, so `client.can_update`. */
  setSignature: (clientId: string, signature: 'EVC' | 'DSC') =>
    api<FilingState>(`/api/v1/filing/${clientId}/signature`, {
      method: 'PUT',
      body: { signature },
    }),
  /**
   * OTPREQUEST. Needs `period.can_update` and `filing.can_update` (the manager's, ND-E12) and a
   * step-up assertion from core-mfa, obtained through `IrreversibleConfirm` (XC-01).
   */
  /** D-13: the client-month's filing status — the lane and what is owed, the session, every
   * submission and every portal call, in sentences. */
  month: (clientId: string, period: string) =>
    api<FilingMonth>(`/api/v1/filing/${clientId}/months/${period}`),
  /** D-11: the portal's 2B, fetched and imported through the same reader as its JSON download. */
  fetch2b: (clientId: string, period: string) =>
    api<PortalFetch>(`/api/v1/filing/${clientId}/months/${period}/2b`, { method: 'POST' }),
  /** D-11: generate the 2B on demand; *Check again* polls and imports it once ready. */
  generate2b: (clientId: string, period: string) =>
    api<PortalFetch>(`/api/v1/filing/${clientId}/months/${period}/2b/generate`, { method: 'POST' }),
  /** ND-E5: the ledger balances as the portal states them; the GSTR-3B takes them first. */
  readLedgers: (clientId: string, period: string) =>
    api<PortalFetch>(`/api/v1/filing/${clientId}/months/${period}/ledgers`, { method: 'POST' }),
  /** D-11: the portal's IMS state with each document's checksum; reads back pushes and resets. */
  readIms: (clientId: string, period: string) =>
    api<PortalFetch>(`/api/v1/filing/${clientId}/months/${period}/ims`, { method: 'POST' }),
  /** D-11: the month's IMS decisions not yet pushed, sent through Save IMS. */
  pushIms: (clientId: string, period: string) =>
    api<PortalFetch>(`/api/v1/filing/${clientId}/months/${period}/ims/push`, { method: 'POST' }),
  /** D-11: documents reset to no action on the portal's IMS; ProGST's decisions stand. */
  resetIms: (clientId: string, period: string, tbRowKeys: string[]) =>
    api<PortalFetch>(`/api/v1/filing/${clientId}/months/${period}/ims/reset`, {
      method: 'POST',
      body: { tb_row_keys: tbRowKeys },
    }),
  /** D-11: GETPREF — monthly or quarterly per quarter, beside ProGST's own record. */
  readPreference: (clientId: string, financialYear: string) =>
    api<PortalPreference>(`/api/v1/filing/${clientId}/preference/${financialYear}`, {
      method: 'POST',
    }),
  /** D-11: SAVEPREF; read the preference back to confirm it. */
  savePreference: (clientId: string, financialYear: string, quarters: Record<string, string>) =>
    api<PortalFetch>(`/api/v1/filing/${clientId}/preference`, {
      method: 'PUT',
      body: { financial_year: financialYear, quarters },
    }),
  /** *Check again* on a call still waiting on the portal. */
  check: (clientId: string, fetchId: string) =>
    api<PortalFetch>(`/api/v1/filing/${clientId}/fetches/${fetchId}/check`, { method: 'POST' }),
  /** *Try again* on a call that did not complete — a new call, made by a person. */
  retry: (clientId: string, fetchId: string) =>
    api<PortalFetch>(`/api/v1/filing/${clientId}/fetches/${fetchId}/retry`, { method: 'POST' }),
  /** D-12: the filed GSTR-1 or GSTR-3B fetched from the portal and kept as its upload is. */
  fetchFiled: (periodId: string) =>
    api<PortalFetch>(`/api/v1/periods/${periodId}/filed/fetch`, { method: 'POST' }),
  requestOtp: (clientId: string, stepUpAssertion?: string) =>
    api<FilingState>(`/api/v1/filing/${clientId}/otp`, { method: 'POST', stepUpAssertion }),
  /** AUTHTOKEN with the OTP the client read out. The OTP is sent once and kept nowhere. */
  openSession: (clientId: string, otp: string, stepUpAssertion?: string) =>
    api<FilingState>(`/api/v1/filing/${clientId}/session`, {
      method: 'POST',
      body: { otp },
      stepUpAssertion,
    }),
  /** D-10: the newest comparison with GSTN's auto-calculated GSTR-3B figures, or a sentence. */
  gstr3bAutocalc: (periodId: string) =>
    api<Gstr3bAutocalc>(`/api/v1/filing/gstr3b/${periodId}/autocalc`),
  /** D-10: AUTOLIAB now, compared with the JSON on file. Changes nothing on either side. */
  fetchGstr3bAutocalc: (periodId: string) =>
    api<Gstr3bAutocalc>(`/api/v1/filing/gstr3b/${periodId}/autocalc`, { method: 'POST' }),
}

/** One figure of the 3B beside GSTN's auto-calculated one (D-10, FR-E35). Paise. */
export interface AutocalcFigure {
  cell: string
  part: string
  ours: number | null
  gstn: number | null
  /** The difference in words; null when the two agree. */
  sentence: string | null
}

export interface Gstr3bAutocalc {
  period_id: string
  fetched: boolean
  words: string
  at: string | null
  /** Fetched for the GSTR-3B JSON on file now. */
  current: boolean
  differences: string[]
  figures: AutocalcFigure[]
}
